import { inject, Injectable } from '@angular/core';
import { CanDeactivate } from '@angular/router';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Observable, of } from 'rxjs';
import { finalize, first, map } from 'rxjs/operators';
import { ClickAction, ClickActionEvent } from '@bn/shared/entities/models';
import { ConfirmationDialogComponent } from '@bn/web-ui/dialog/components/confirmation-dialog/confirmation-dialog.component';

export interface CanComponentDeactivate {
  canDeactivate: () => Observable<boolean> | boolean;
}

/**
 * This guard checks whether it's safe to navigate away from a route/component.
 * If the component has a `canDeactivate` method, it will be called to determine
 * if navigation can proceed. If the method returns false or an observable that
 * resolves to false, a confirmation dialog will prompt the user to confirm
 * navigation. Otherwise, navigation is allowed.
 *
 * @returns {Observable<boolean> | boolean}
 * A boolean value or an Observable that resolves to true (allow navigation)
 * or false (prevent navigation).
 *
 * @example
 * ```ts
 * export class MyComponent implements CanComponentDeactivate {
 *   @ViewChild('optimizerForm') public optimizerForm: NgForm;
 *
 *   public canDeactivate(): Observable<boolean> | boolean {
 *     return !this.optimizerForm.dirty;
 *   }
 * }
 *
 * // Used with UnsavedChangesGuard in routing:
 * {
 *   path: 'my-route',
 *   component: MyComponent,
 *   canDeactivate: [UnsavedChangesGuard]
 * }
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class UnsavedChangesGuard implements CanDeactivate<CanComponentDeactivate> {
  #dialog = inject(BsModalService);

  canDeactivate(component: CanComponentDeactivate): Observable<boolean> | boolean {
    if (component?.canDeactivate) {
      const canLeave = component.canDeactivate();
      if (canLeave instanceof Observable) {
        return canLeave;
      } else if (!canLeave) {
        return this.showDiscardUnsavedChangesConfirmDialog();
      }
    }
    return true;
  }

  /**
   * Displays a confirmation dialog to the user asking if they are sure they want to leave the page
   * and discard all unsaved changes.
   *
   * This method is used to prompt the user for confirmation before navigating away from a page
   * where unsaved changes may exist. If the user confirms the action, it returns an observable
   * that emits `true`; otherwise, it emits `false`.
   *
   * @returns An observable that emits a boolean value indicating whether the user confirmed
   *          the action (`true` for confirmation, `false` for cancellation).
   *
   * @example
   * // Example usage:
   * this.showDiscardUnsavedChangesConfirmDialog().subscribe((confirmed) => {
   *   if (confirmed) {
   *     // Proceed with navigation or discard changes
   *   } else {
   *     // Stay on the current page
   *   }
   * });
   */
  private showDiscardUnsavedChangesConfirmDialog(): Observable<boolean> {
    return (
      this.#dialog
        .show(ConfirmationDialogComponent, {
          id: 'discardUnsavedChangesConfirmDialog',
          class: 'modal-dialog-centered modal-sm',
          initialState: {
            data: {
              confirmButtonName: 'Yes, Close It!',
              description: `Are you sure you want to leave this page and discard all changes?`,
              event: {
                action: 'confirm',
              },
              title: 'Discard all changes',
              textCenter: true,
            },
          },
        })
        .content?.getOnAction$()
        .pipe(
          first(), // one time
          finalize(() => this.#dialog.hide()), // always
          map((event: ClickActionEvent) => event.action === ClickAction.confirm),
        ) ?? of(false)
    );
  }
}
